import { Node, Edge, Position, useReactFlow } from "reactflow";
import dagre from "@dagrejs/dagre";
import { nodeWidth, nodeHeight, STREAM_NODE, LAYOUT_CONFIG, Layouts, type Layout } from "./flow.constants";
import { getSugiyamaLayoutWithVisualOffset } from "./sugiyama.layout.utils";
import store from "src/stores/redux/store";
import { getAppFQN } from "./selectors";
import { loadByApplication } from "app/components/flow/designer/nodesPositionStorage";

// ============================================================================
// DAGRE LAYOUT UTILITIES
// ============================================================================

// Create a fresh Dagre graph for each layout calculation to ensure deterministic results
const createFreshDagreGraph = () => {
    return new dagre.graphlib.Graph().setDefaultEdgeLabel(() => ({}));
};

/**
 * Applies automatic layout to nodes and edges using Dagre
 */
export const getLayoutedElements = (nodes: Node[], edges: Edge[], direction: Layout = Layouts.VERTICAL) => {
    // Create a fresh Dagre graph for each layout calculation to ensure deterministic results
    const dagreGraph = createFreshDagreGraph();
    const isHorizontal = direction === Layouts.HORIZONTAL;

    // Configure graph layout with proper spacing
    dagreGraph.setGraph({
        rankdir: direction,
        nodesep: isHorizontal
            ? LAYOUT_CONFIG.DAGRE_NODE_SEPARATION.HORIZONTAL
            : LAYOUT_CONFIG.DAGRE_NODE_SEPARATION.VERTICAL,
        ranksep: isHorizontal
            ? LAYOUT_CONFIG.DAGRE_RANK_SEPARATION.HORIZONTAL
            : LAYOUT_CONFIG.DAGRE_RANK_SEPARATION.VERTICAL,
        marginx: LAYOUT_CONFIG.DAGRE_MARGINS.X,
        marginy: LAYOUT_CONFIG.DAGRE_MARGINS.Y
    });

    nodes.forEach(node => {
        // Use different dimensions for stream nodes
        const width = node.type === "STREAM" ? STREAM_NODE.WIDTH : nodeWidth;
        const height = node.type === "STREAM" ? STREAM_NODE.HEIGHT : nodeHeight;
        dagreGraph.setNode(node.id, { width, height });
    });

    edges.forEach(edge => {
        dagreGraph.setEdge(edge.source, edge.target);
    });

    // Apply Dagre layout algorithm
    dagre.layout(dagreGraph);

    const newNodes = nodes.map(node => {
        const nodeWithPosition = dagreGraph.node(node.id);
        const width = node.type === "STREAM" ? STREAM_NODE.WIDTH : nodeWidth;
        const height = node.type === "STREAM" ? STREAM_NODE.LAYOUT_HEIGHT : nodeHeight;

        const finalPosition = {
            x: nodeWithPosition.x - width / 2,
            y: nodeWithPosition.y - height / 2
        };

        return {
            ...node,
            targetPosition: isHorizontal ? Position.Left : Position.Top,
            sourcePosition: isHorizontal ? Position.Right : Position.Bottom,
            // We are shifting the dagre node position (anchor=center center) to the top left
            // so it matches the React Flow node anchor point (top left).
            position: finalPosition
        };
    });

    return { nodes: newNodes, edges };
};

// ============================================================================
// GRID LAYOUT UTILITIES
// ============================================================================

/**
 * Calculates position for nodes in a grid layout
 */
export const calculateGridPosition = (
    index: number,
    maxComponentsPerRow: number = LAYOUT_CONFIG.MAX_COMPONENTS_PER_ROW,
    horizontalSpacing: number = LAYOUT_CONFIG.HORIZONTAL_SPACING,
    verticalSpacing: number = LAYOUT_CONFIG.VERTICAL_SPACING,
    initialXOffset: number = LAYOUT_CONFIG.INITIAL_X_OFFSET,
    initialYOffset: number = LAYOUT_CONFIG.INITIAL_Y_OFFSET
): { x: number; y: number } => {
    const row = Math.floor(index / maxComponentsPerRow);
    const col = index % maxComponentsPerRow;

    return {
        x: initialXOffset + col * horizontalSpacing,
        y: initialYOffset + row * verticalSpacing
    };
};

/**
 * Calculates the next position for a component in a grid layout
 */
export const getNextGridPosition = (
    currentX: number,
    currentY: number,
    componentsInRow: number,
    maxComponentsPerRow: number = LAYOUT_CONFIG.MAX_COMPONENTS_PER_ROW,
    horizontalSpacing: number = LAYOUT_CONFIG.HORIZONTAL_SPACING,
    verticalSpacing: number = LAYOUT_CONFIG.VERTICAL_SPACING,
    initialXOffset: number = LAYOUT_CONFIG.INITIAL_X_OFFSET
): { x: number; y: number; componentsInRow: number } => {
    let newX = currentX;
    let newY = currentY;
    let newComponentsInRow = componentsInRow;

    if (componentsInRow >= maxComponentsPerRow) {
        newComponentsInRow = 0;
        newY += verticalSpacing;
        newX = initialXOffset;
    }

    const position = { x: newX, y: newY };
    newX += horizontalSpacing;
    newComponentsInRow++;

    return { ...position, componentsInRow: newComponentsInRow };
};

export const getArrangedNodes = (
    existingNodes: Node[],
    existingEdges: Edge[],
    currentLayout: Layout,
    algorithm: "dagre" | "sugiyama" = "sugiyama",
    useSavedPositions: boolean = false
): { nodes: Node[]; edges: Edge[] } => {
    let savedPositions: Record<string, { left: number; top: number }> = {};

    if (useSavedPositions) {
        const appFQN = getAppFQN(store.getState());

        if (!appFQN) {
            return getArrangedNodes(existingNodes, existingEdges, currentLayout, algorithm);
        }

        savedPositions = loadByApplication(appFQN);
    }

    const { nodes, edges } = getLayoutedElements(existingNodes, existingEdges, currentLayout ?? Layouts.VERTICAL);

    if (algorithm === "sugiyama") {
        const sugiyamaLayout = getSugiyamaLayoutWithVisualOffset(nodes, edges);

        const getCoordinates = (id: string, type: string | undefined) => {
            const Ratio = { Height: 190, Width: 300 };
            const [sx, sy] = sugiyamaLayout[id].coords;
            if (type === "STREAM") {
                return {
                    x: sx * Ratio.Width + 104,
                    y: sy * Ratio.Height + 105
                };
            }
            return {
                x: sx * Ratio.Width,
                y: sy * Ratio.Height
            };
        };
        nodes.forEach(node => {
            node.position = getCoordinates(node.id, node.type);
        });
    }
    nodes.forEach(node => {
        if (useSavedPositions && savedPositions[node.id]) {
            node.position = {
                x: savedPositions[node.id].left,
                y: savedPositions[node.id].top
            };
        }
    });
    return { nodes, edges };
};

const getMaxPositionXAndY = (nodes: Node[]) => {
    return nodes.reduce(
        (max, node) => {
            max.x = Math.max(max.x, node.position.x);
            max.y = Math.max(max.y, node.position.y);
            return max;
        },
        { x: 0, y: 0 }
    );
};

export const moveFirstNodeToCenter = (
    nodes: Node[],
    setCenter: (x: number, y: number, options?: { zoom?: number; duration?: number }) => void,
    fitView: (options?: { duration?: number }) => void,
    direction: Layout = Layouts.VERTICAL
) => {
    const { x: maxX, y: maxY } = getMaxPositionXAndY(nodes);

    if (maxX < 2500 && maxY < 2500) {
        fitView({ duration: 500 });
        return;
    }

    const isHorizontal = direction === Layouts.HORIZONTAL;

    // Find the first node based on layout direction
    const firstNode = nodes.reduce((first, node) => {
        if (!first) return node;

        if (isHorizontal) {
            // For horizontal layout (LR): leftmost node, then topmost if same x position
            if (node.position.x < first.position.x) return node;
            if (node.position.x === first.position.x && node.position.y < first.position.y) return node;
        } else {
            // For vertical layout (TB): topmost node, then leftmost if same y position
            if (node.position.y < first.position.y) return node;
            if (node.position.y === first.position.y && node.position.x < first.position.x) return node;
        }

        return first;
    }, null as Node | null);

    if (firstNode) {
        setCenter(
            firstNode.position.x + (isHorizontal ? 500 : 300),
            firstNode.position.y + (isHorizontal ? 150 : 400),
            { zoom: 0.75, duration: 400 }
        );
    }
};
